import { useMemo } from 'react'
import { sortBy } from 'lodash'
import { useRateConvert, useTickerBatch } from '@web/walter-lib/stream'
import { useCommonCMSConfig, useCurrencies, useProviders, useSymbols } from '~api'
import { processProvidersInfo } from '~utils/data/processProviders'
import { processCoins } from '~utils/data/processCoins'
import { useFiatMockRate } from '~hooks/useFiatMockRate'
import { USD_CURRENCY_CODE } from '~constants/currency'
import { useRatesDataMap } from '~hooks/useRatesMap'
import { useBaseCurrency } from '~hooks/useBaseCurrency'
import { SymbolInfo } from '~hooks/useBlocksData'
import { usePrevCurrentRatesBatch } from '~hooks/usePrevCurrentRatesBatch'

const usePrices = () => {
    const { data } = useCommonCMSConfig()
    const { isNewRateService } = data?.features ?? {}

    const { data: currencies = {}, isLoading } = useCurrencies()

    const { calculatedBaseCurrency: baseCurrency } = useBaseCurrency()
    const rateConvert = useRateConvert()
    const { data: tickers } = useTickerBatch({ symbols: ['*'], speed: '3s' })

    const baseCurrencies = Object.keys(currencies)

    const {
        ratesData,
        prevRatesData,
        isLoading: isRatesLoading,
    } = usePrevCurrentRatesBatch({
        isNewRateService,
        currencies: baseCurrencies,
        quote: baseCurrency,
    })

    const ratesDataMap = useRatesDataMap({
        isNewRateService: Boolean(isNewRateService),
        ratesData,
        currencies,
        prevRatesData,
    })

    const prices = useMemo(
        () =>
            baseCurrencies?.reduce((acc, code) => {
                let price = null
                if (isNewRateService) {
                    price = ratesDataMap[code]?.price ?? null
                } else {
                    const revertRateClose = rateConvert?.getRate(baseCurrency, code)
                    const rateClose = revertRateClose ? 1 / revertRateClose : null

                    price = rateClose != null ? String(rateClose) : null
                }

                acc[code] = price

                return acc
            }, {}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tickers, baseCurrencies, baseCurrency, isNewRateService, rateConvert, ratesDataMap]
    )

    return {
        data: prices,
        isLoading: isRatesLoading || isLoading,
    }
}

export const useData = () => {
    const { data, isValidating: isCmsLoading } = useCommonCMSConfig()
    const { isNewRateService } = data?.features ?? {}
    const { data: currencies, isValidating: isCurrenciesLoading } = useCurrencies()
    const { data: symbols, isValidating: isSymbolsLoading } = useSymbols()

    const { data: providers, isValidating: isProvidersLoading } = useProviders()
    const { targetObj } = useMemo(() => processProvidersInfo(providers), [providers])
    const targetBuyCurrencies = useMemo(() => new Set(Object.keys(targetObj)), [targetObj])

    const { data: prices, isLoading: isPricesLoading } = usePrices()

    const { info } = useMemo(() => {
        return processCoins({
            currencies,
            symbols,
            buyCurrencies: targetBuyCurrencies,
            isNewRateService: Boolean(!isCmsLoading && isNewRateService),
        })
    }, [currencies, symbols, targetBuyCurrencies, isNewRateService, isCmsLoading])

    const { coinsWithPrices, coinsWithNoPrices } = useMemo(() => {
        const coinsWithPrices: SymbolInfo[] = []
        const coinsWithNoPrices: SymbolInfo[] = []

        info?.forEach(item => {
            if (prices?.[item.code] != null) {
                coinsWithPrices.push(item)
            } else {
                coinsWithNoPrices.push(item)
            }
        })

        return {
            coinsWithPrices,
            coinsWithNoPrices,
        }
    }, [info, prices])

    const sortedPriceInfo = useMemo(
        () => sortBy(coinsWithPrices, [item => currencies?.[item.code]?.account_top_order, item => item.name]),
        [coinsWithPrices, currencies]
    )

    const sortedNoPriceInfo = useMemo(
        () => sortBy(coinsWithNoPrices, [item => currencies?.[item.code]?.account_top_order, item => item.name]),
        [coinsWithNoPrices, currencies]
    )

    const sortedInfo = useMemo(() => {
        return [...sortedPriceInfo, ...sortedNoPriceInfo]
    }, [sortedPriceInfo, sortedNoPriceInfo])

    const hasUsd = info.some(item => item.code === USD_CURRENCY_CODE)
    useFiatMockRate({ currencyCode: hasUsd ? USD_CURRENCY_CODE : undefined })

    return {
        data: sortedInfo,
        isLoading: isCurrenciesLoading || isSymbolsLoading || isProvidersLoading || isCmsLoading || isPricesLoading,
    }
}
